import { compact } from 'lodash';

import { IntegrationClientKind } from '@/types/schema';

import { getProviderDisplayNameFromIntegrationClientKind } from '../../assetProviderIntegrations.utils';
import { IntegrationClientsTypeahead_IntegrationClientFragment } from './graphql/IntegrationClientTypeahead.generated';
import { InitialSyncStatus } from './hooks/useTrackInitialAssetIntegrationSync';
import { INTEGRATION_CLIENT_EXTERNAL_KIND_DISPLAY } from './IntegrationClientTypeahead.constants';

interface GetHelpTextForStatusOptions {
  status: InitialSyncStatus;
  notLoggedIn?: boolean;
}

export function getHelpTextForStatus({
  status,
  notLoggedIn,
}: GetHelpTextForStatusOptions) {
  if (notLoggedIn) {
    return `You have not linked any integration accounts. Go to manage profile under user settings to link.`;
  }

  switch (status) {
    case InitialSyncStatus.PENDING:
      return `Syncing clients and entities from integrations. This may take a few minutes, depending on how many clients and entities you have in your provider.`;
    case InitialSyncStatus.COMPLETE:
      return `Successfully synced clients and entities`;
    default:
      return undefined;
  }
}

interface GetBadgeTextOptions {
  shouldShowAddeparKindBadge: boolean;
  shouldShowProviderBadge: boolean;
}

export function getBadgeText(
  integrationClient: IntegrationClientsTypeahead_IntegrationClientFragment,
  { shouldShowAddeparKindBadge, shouldShowProviderBadge }: GetBadgeTextOptions
): string | undefined {
  // if there are both multiple providers AND we're importing multiple different kinds of clients from
  // addepar, we want to show the provider name badge for non-addepar clients, and SPECIAL
  // addepar + addepar kind badge for addepar clients
  if (shouldShowAddeparKindBadge && shouldShowProviderBadge) {
    const providerName = getProviderDisplayNameFromIntegrationClientKind(
      integrationClient.kind
    );
    if (integrationClient.kind === IntegrationClientKind.Addepar) {
      const externalKindDisplay = integrationClient.externalKind
        ? INTEGRATION_CLIENT_EXTERNAL_KIND_DISPLAY[
            integrationClient.externalKind
          ]
        : null;
      return compact([providerName, externalKindDisplay]).join(' ');
    }
    return providerName;
  }

  // otherwise, we just show the addepar kind badge
  if (shouldShowAddeparKindBadge && integrationClient.externalKind) {
    // Show Addepar external kind badge when only Addepar is enabled
    return INTEGRATION_CLIENT_EXTERNAL_KIND_DISPLAY[
      integrationClient.externalKind
    ];
  }

  // otherwise, we just show the provider name
  if (shouldShowProviderBadge && integrationClient.kind) {
    // Show provider name when multiple providers are enabled
    return getProviderDisplayNameFromIntegrationClientKind(
      integrationClient.kind
    );
  }

  return undefined;
}
